import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Translation resources
const resources = {
  de: {
    translation: {
      // Navigation
      nav: {
        search: "Suchen",
        offer: "Anbieten",
        tips: "Tipps",
        myListings: "Meine Anzeigen",
        login: "Anmelden",
        register: "Registrieren",
        myAccount: "Mein Konto",
        menu: "Menü",
        logout: "Abmelden",
        welcome: "Willkommen, {{name}}",
        darkMode: "Dunkelmodus aktivieren",
        lightMode: "Hellmodus aktivieren",
      },
      auth: {
        loginTitle: "Anmelden",
        loginDescription: "Melde dich an, um Anzeigen zu erstellen und zu verwalten.",
        loginButton: "Mit WG-Anzeigen Account anmelden",
        loginHeroEyebrow: "Gemeinsam wohnen, gemeinsam sparen",
        loginHeroTitle: "Willkommen zurück bei WG-Anzeigen",
        loginHeroSubtitle: "Verwalte deine Inserate und finde passende Mitbewohner:innen in einer Oberfläche.",
        loginBenefitListingsTitle: "Alle Inserate im Blick",
        loginBenefitListingsDescription: "Bearbeite bestehende Anzeigen oder starte neue Gesuche in wenigen Klicks.",
        loginBenefitSecurityTitle: "Vertrauen & Sicherheit",
        loginBenefitSecurityDescription: "Sichere Kommunikation mit verifizierten Kontakten aus der WG-Anzeigen-Community.",
        loginBenefitSupportTitle: "Persönlicher Support",
        loginBenefitSupportDescription: "Unser Team hilft dir bei Fragen zu deinem Inserat oder zur Plattform.",
        loginHeroFooter: "Dein Zugang zu seriösen WG-Angeboten in ganz Deutschland.",
        forgotPassword: "Passwort vergessen?",
        noAccountYet: "Noch keinen Account?",
        registerCta: "Jetzt registrieren",
        registerTitle: "Account erstellen",
        registerDescription: "Registriere dich kostenlos, um eigene WG-Anzeigen zu veröffentlichen.",
        registerButton: "Account erstellen",
        haveAccount: "Bereits einen Account?",
        backToLogin: "Zur Anmeldung",
      },
      // Header
      header: {
        title: "WG-Anzeigen.de",
        subtitle: "Finde dein WG-Zimmer schnell und einfach",
        searchPlaceholder: "Stadt, PLZ oder Region eingeben",
        roomType: "Zimmer / WG-Zimmer",
        priceUpTo: "Preis bis",
        findOffers: "Angebote finden",
      },
      // Search
      search: {
        location: "Ort",
        roomType: "Zimmertyp",
        rentType: "Mietart",
        minSize: "Min. Größe m²",
        maxPrice: "Max. Miete €",
        maxPricePlaceholder: "z. B. 800",
        minSizePlaceholder: "z. B. 15",
        furnished: "Möbliert",
        availableFrom: "Verfügbar ab",
        moreFilters: "Weitere Filter",
        applyFilters: "Filter anwenden",
        wgSize: "WG-Größe",
        any: "Egal",
        popularCitiesLabel: "Beliebte Städte:",
        clearAllFilters: "Alle Filter löschen",
      },
      // Listings
      listings: {
        room: "Zimmer",
        wg: "WG",
        available: "Verfügbar",
        size: "Größe",
        price: "Preis",
        furnished: "Möbliert",
        unfurnished: "Unmöbliert",
        contact: "Kontakt",
        verified: "Verifiziert",
        online: "Online",
        description: "Beschreibung",
        details: "Details",
        availableFrom: "Verfügbar ab",
        duration: "Mietdauer",
        contactNow: "Jetzt kontaktieren",
        contactInfo: "Fülle das Formular aus, um den Vermieter zu kontaktieren.",
        contactForm: {
          title: "Kontakt aufnehmen",
          subtitle:
            "Stelle dich kurz vor und beschreibe dein Interesse an dem Zimmer.",
          nameLabel: "Dein Name",
          emailLabel: "Deine E-Mail",
          messageLabel: "Nachricht",
          messagePlaceholder: "Hallo, ich interessiere mich für das Zimmer...",
          submit: "Nachricht senden",
          submitting: "Senden...",
          cancel: "Abbrechen",
          success: "Deine Nachricht wurde erfolgreich versendet.",
          errors: {
            nameRequired: "Bitte gib deinen Namen ein.",
            emailRequired: "Bitte gib deine E-Mail-Adresse ein.",
            emailInvalid: "Bitte gib eine gültige E-Mail-Adresse ein.",
            messageRequired: "Bitte gib eine Nachricht ein.",
            submitFailed:
              "Senden der Nachricht fehlgeschlagen. Bitte versuche es erneut.",
          },
        },
        features: "Ausstattung",
        roomSize: "Zimmergröße",
        wifi: "WLAN",
        parking: "Parkplatz",
        parkingAvailable: "Verfügbar",
        pets: "Haustiere",
        petsAllowed: "Erlaubt",
        smoking: "Rauchen",
        smokingAllowed: "Erlaubt",
        balcony: "Balkon",
        balconyAvailable: "Verfügbar",
        minutes: "Minuten",
        hours: "Stunden",
        days: "Tage",
      },
      // Footer
      footer: {
        copyright: "© 2024 WG-Anzeigen.de — Fair, schnell, ohne Tracking.",
        about: "Über uns",
        help: "Hilfe",
        contact: "Kontakt",
        privacy: "Datenschutz",
        terms: "AGB",
        imprint: "Impressum",
      },
      // Common
      common: {
        loading: "Lädt...",
        error: "Fehler",
        noResults: "Keine Ergebnisse gefunden",
        tryAgain: "Erneut versuchen",
        back: "Zurück",
        next: "Weiter",
        previous: "Zurück",
        page: "Seite",
        of: "von",
        yes: "Ja",
        no: "Nein",
        included: "Inklusive",
      },
      tips: {
        heroTitle: "WG-Tipps & Ratgeber",
        heroSubtitle: "Alles was Sie über die WG-Suche und das WG-Leben wissen müssen",
        alertTitle: "Sicherheitstipp",
        alertDescription:
          "Seien Sie immer vorsichtig bei WG-Anzeigen. Besichtigen Sie persönlich und zahlen Sie niemals im Voraus ohne Vertrag!",
        sections: {
          search: {
            title: "Tipps zur WG-Suche",
            items: [
              {
                title: "Frühzeitig suchen",
                description:
                  "Beginnen Sie mindestens 2-3 Monate vor dem gewünschten Einzugstermin mit der Suche.",
              },
              {
                title: "Vollständige Bewerbung",
                description:
                  "Erstellen Sie ein aussagekräftiges Profil mit Foto und persönlicher Vorstellung.",
              },
              {
                title: "Flexibel sein",
                description: "Seien Sie offen für verschiedene Stadtteile und WG-Größen.",
              },
              {
                title: "Schnell reagieren",
                description:
                  "Antworten Sie schnell auf interessante Angebote – gute Zimmer sind schnell weg.",
              },
            ],
          },
          security: {
            title: "Sicherheit & Betrugsschutz",
            items: [
              {
                title: "Besichtigung vor Vertrag",
                description: "Besichtigen Sie das Zimmer immer persönlich vor Vertragsabschluss.",
              },
              {
                title: "Vertrag prüfen",
                description:
                  "Lassen Sie den Mietvertrag vor Unterzeichnung prüfen (z.B. durch Mieterverein).",
              },
              {
                title: "Vorsicht bei Vorauszahlungen",
                description: "Zahlen Sie niemals im Voraus ohne Vertrag oder bei unseriösen Anbietern.",
              },
              {
                title: "Persönliche Treffen",
                description:
                  "Vermeiden Sie Geschäfte nur über E-Mail oder WhatsApp ohne persönlichen Kontakt.",
              },
            ],
          },
          wgLife: {
            title: "Harmonisches WG-Leben",
            items: [
              {
                title: "Klare Absprachen",
                description:
                  "Besprechen Sie Putzpläne, Einkäufe und gemeinsame Regeln von Anfang an.",
              },
              {
                title: "Kommunikation",
                description: "Reden Sie Probleme offen an, bevor sie zu Konflikten werden.",
              },
              {
                title: "Gemeinsame Aktivitäten",
                description:
                  "Organisieren Sie regelmäßige WG-Abende zum Kennenlernen und Zusammenhalt.",
              },
              {
                title: "Respekt",
                description:
                  "Respektieren Sie die Privatsphäre und Gewohnheiten Ihrer Mitbewohner.",
              },
            ],
          },
          costs: {
            title: "Kosten & Finanzen",
            items: [
              {
                title: "Nebenkosten beachten",
                description:
                  "Kalkulieren Sie Strom, Internet, GEZ und andere Nebenkosten in Ihr Budget ein.",
              },
              {
                title: "Kaution sparen",
                description: "Planen Sie 2-3 Monatsmieten als Kaution ein.",
              },
              {
                title: "Einrichtungskosten",
                description:
                  "Berücksichtigen Sie Kosten für Möbel, Küchenausstattung und Haushaltsgeräte.",
              },
              {
                title: "Versicherungen",
                description: "Informieren Sie sich über Haftpflicht- und Hausratversicherung.",
              },
            ],
          },
          faq: {
            title: "Häufige Fragen (FAQ)",
            items: [
              {
                question: "Wie viel sollte ich für ein WG-Zimmer ausgeben?",
                answer:
                  "Als Faustregel gilt: Maximal 30% Ihres Nettoeinkommens für die Miete. In Großstädten sind 400-800€ für ein WG-Zimmer normal.",
              },
              {
                question: "Was gehört in eine gute WG-Bewerbung?",
                answer:
                  "Ein freundliches Foto, eine persönliche Vorstellung, Ihre Hobbys, Studienrichtung/Beruf und warum Sie in diese WG passen.",
              },
              {
                question: "Wie erkenne ich unseriöse Angebote?",
                answer:
                  "Vorsicht bei ungewöhnlich günstigen Preisen, Vorauszahlungen ohne Vertrag, fehlenden Kontaktdaten oder Druck zum schnellen Abschluss.",
              },
              {
                question: "Was sollte ich bei der Besichtigung beachten?",
                answer:
                  "Prüfen Sie die Lage, Verkehrsanbindung, Zustand des Zimmers, Gemeinschaftsräume, Mitbewohner und klären Sie alle wichtigen Fragen.",
              },
              {
                question: "Wie löse ich Konflikte in der WG?",
                answer:
                  "Kommunizieren Sie offen, hören Sie zu, suchen Sie Kompromisse und organisieren Sie regelmäßige WG-Gespräche.",
              },
            ],
          },
          quick: {
            title: "Schnell-Tipps",
            items: [
              "Erstellen Sie ein ansprechendes Profilbild",
              "Schreiben Sie eine persönliche Bewerbung",
              "Seien Sie pünktlich bei Besichtigungen",
              "Stellen Sie Fragen zur WG und den Mitbewohnern",
              "Bringen Sie alle wichtigen Unterlagen mit",
              "Seien Sie ehrlich über Ihre Gewohnheiten",
              "Zeigen Sie Interesse an der WG-Gemeinschaft",
              "Bleiben Sie höflich auch bei Absagen",
            ],
          },
          contact: {
            title: "Haben Sie weitere Fragen?",
            description:
              "Kontaktieren Sie uns gerne für weitere Tipps und Unterstützung bei Ihrer WG-Suche.",
            cta: "Kontakt aufnehmen",
            ariaLabel: "Kontakt per E-Mail aufnehmen",
          },
        },
      },
      // 404 Page
      notFound: {
        title: "Seite nicht gefunden",
        description: "Die gesuchte Seite existiert leider nicht oder wurde verschoben.",
        goHome: "Zur Startseite",
        search: "WG suchen",
        goBack: "Zurück",
        helpfulLinks: "Hilfreiche Links",
        searchDescription: "Finde dein WG-Zimmer",
        tipsDescription: "Tipps für die WG-Suche",
        contactInfo: "Probleme? Kontaktiere uns gerne!",
      },
    },
  },
  en: {
    translation: {
      // Navigation
      nav: {
        search: "Search",
        offer: "Offer",
        tips: "Tips",
        myListings: "My listings",
        login: "Login",
        register: "Register",
        myAccount: "My Account",
        menu: "Menu",
        logout: "Logout",
        welcome: "Welcome, {{name}}",
        darkMode: "Enable dark mode",
        lightMode: "Enable light mode",
      },
      auth: {
        loginTitle: "Log in",
        loginDescription: "Sign in to create and manage your listings.",
        loginButton: "Sign in with your WG-Anzeigen account",
        loginHeroEyebrow: "Live together, save together",
        loginHeroTitle: "Welcome back to WG-Anzeigen",
        loginHeroSubtitle: "Manage your listings and meet future flatmates in one dashboard.",
        loginBenefitListingsTitle: "Everything in one place",
        loginBenefitListingsDescription: "Edit existing offers or create new requests in just a few clicks.",
        loginBenefitSecurityTitle: "Trusted & secure",
        loginBenefitSecurityDescription: "Secure messaging with verified members of the WG-Anzeigen community.",
        loginBenefitSupportTitle: "Human support",
        loginBenefitSupportDescription: "Our team is ready to help with your listings or platform questions.",
        loginHeroFooter: "Your gateway to trusted flatshare offers across Germany.",
        forgotPassword: "Forgot password?",
        noAccountYet: "Don't have an account yet?",
        registerCta: "Create one now",
        registerTitle: "Create account",
        registerDescription: "Register for free to publish your own flatshare listings.",
        registerButton: "Create account",
        haveAccount: "Already have an account?",
        backToLogin: "Back to login",
      },
      // Header
      header: {
        title: "WG-Anzeigen.de",
        subtitle: "Find your shared apartment room quickly and easily",
        searchPlaceholder: "Enter city, postal code or region",
        roomType: "Room / Shared Apartment",
        priceUpTo: "Price up to",
        findOffers: "Find offers",
      },
      // Search
      search: {
        location: "Location",
        roomType: "Room Type",
        rentType: "Rent Type",
        minSize: "Min. Size m²",
        maxPrice: "Max. Rent €",
        maxPricePlaceholder: "e.g. 800",
        minSizePlaceholder: "e.g. 15",
        furnished: "Furnished",
        availableFrom: "Available from",
        moreFilters: "More Filters",
        applyFilters: "Apply Filters",
        wgSize: "WG size",
        any: "Any",
        popularCitiesLabel: "Popular cities:",
        clearAllFilters: "Clear all filters",
      },
      // Listings
      listings: {
        room: "Room",
        wg: "Shared Apartment",
        available: "Available",
        size: "Size",
        price: "Price",
        furnished: "Furnished",
        unfurnished: "Unfurnished",
        contact: "Contact",
        verified: "Verified",
        online: "Online",
        description: "Description",
        details: "Details",
        availableFrom: "Available from",
        duration: "Duration",
        contactNow: "Contact now",
        contactInfo: "Fill out the form to get in touch with the landlord.",
        contactForm: {
          title: "Get in touch",
          subtitle:
            "Introduce yourself briefly and describe your interest in the room.",
          nameLabel: "Your name",
          emailLabel: "Your email",
          messageLabel: "Message",
          messagePlaceholder: "Hi, I'm interested in the room...",
          submit: "Send message",
          submitting: "Sending...",
          cancel: "Cancel",
          success: "Your message was sent successfully.",
          errors: {
            nameRequired: "Please enter your name.",
            emailRequired: "Please enter your email address.",
            emailInvalid: "Please enter a valid email address.",
            messageRequired: "Please enter a message.",
            submitFailed:
              "Failed to send your message. Please try again.",
          },
        },
        features: "Features",
        roomSize: "Room size",
        wifi: "WiFi",
        parking: "Parking",
        parkingAvailable: "Available",
        pets: "Pets",
        petsAllowed: "Allowed",
        smoking: "Smoking",
        smokingAllowed: "Allowed",
        balcony: "Balcony",
        balconyAvailable: "Available",
        minutes: "minutes",
        hours: "hours",
        days: "days",
      },
      // Footer
      footer: {
        copyright: "© 2024 WG-Anzeigen.de — Fair, fast, no tracking.",
        about: "About us",
        help: "Help",
        contact: "Contact",
        privacy: "Privacy",
        terms: "Terms",
        imprint: "Imprint",
      },
      // Common
      common: {
        loading: "Loading...",
        error: "Error",
        noResults: "No results found",
        tryAgain: "Try again",
        back: "Back",
        next: "Next",
        previous: "Previous",
        page: "Page",
        of: "of",
        yes: "Yes",
        no: "No",
        included: "Included",
      },
      tips: {
        heroTitle: "Flatshare tips & advice",
        heroSubtitle: "Everything you need to know about finding and living in a shared flat",
        alertTitle: "Safety tip",
        alertDescription:
          "Always be cautious with flatshare listings. Visit in person and never pay upfront without a contract!",
        sections: {
          search: {
            title: "Tips for your flatshare search",
            items: [
              {
                title: "Start searching early",
                description:
                  "Begin looking at least 2-3 months before your desired move-in date.",
              },
              {
                title: "Complete application",
                description:
                  "Create a compelling profile with a photo and personal introduction.",
              },
              {
                title: "Stay flexible",
                description:
                  "Be open to different neighbourhoods and shared flat sizes.",
              },
              {
                title: "Respond quickly",
                description:
                  "Answer interesting offers right away – good rooms go fast.",
              },
            ],
          },
          security: {
            title: "Safety & fraud protection",
            items: [
              {
                title: "View before signing",
                description: "Always visit the room in person before signing a contract.",
              },
              {
                title: "Check the contract",
                description:
                  "Have the rental agreement reviewed before signing (e.g. by a tenants' association).",
              },
              {
                title: "Beware of advance payments",
                description:
                  "Never pay in advance without a contract or if the offer seems suspicious.",
              },
              {
                title: "Meet in person",
                description:
                  "Avoid deals conducted only via email or messaging without personal contact.",
              },
            ],
          },
          wgLife: {
            title: "Harmonious shared living",
            items: [
              {
                title: "Set clear agreements",
                description:
                  "Discuss cleaning schedules, shopping and shared rules from the start.",
              },
              {
                title: "Communicate",
                description: "Address issues openly before they turn into conflicts.",
              },
              {
                title: "Plan joint activities",
                description:
                  "Organise regular flat evenings to get to know each other and build a community.",
              },
              {
                title: "Show respect",
                description:
                  "Respect your flatmates' privacy and habits.",
              },
            ],
          },
          costs: {
            title: "Costs & finances",
            items: [
              {
                title: "Keep an eye on utilities",
                description:
                  "Include electricity, internet, TV licence and other costs in your budget.",
              },
              {
                title: "Save for the deposit",
                description: "Plan for a deposit of 2-3 months' rent.",
              },
              {
                title: "Furniture expenses",
                description:
                  "Consider costs for furniture, kitchen equipment and household appliances.",
              },
              {
                title: "Insurance",
                description:
                  "Look into liability and household contents insurance.",
              },
            ],
          },
          faq: {
            title: "Frequently asked questions (FAQ)",
            items: [
              {
                question: "How much should I spend on a flatshare room?",
                answer:
                  "As a rule of thumb, spend no more than 30% of your net income on rent. In big cities, €400-800 for a room is common.",
              },
              {
                question: "What makes a good flatshare application?",
                answer:
                  "Include a friendly photo, a personal introduction, your hobbies, studies or profession, and why you fit the flatshare.",
              },
              {
                question: "How do I spot fraudulent offers?",
                answer:
                  "Be cautious of unusually low prices, advance payments without a contract, missing contact details or pressure to decide quickly.",
              },
              {
                question: "What should I check during a viewing?",
                answer:
                  "Assess the location, transport links, room condition, shared spaces, flatmates and clarify all important questions.",
              },
              {
                question: "How can I resolve conflicts in the flatshare?",
                answer:
                  "Communicate openly, listen, look for compromises and schedule regular flat meetings.",
              },
            ],
          },
          quick: {
            title: "Quick tips",
            items: [
              "Create an appealing profile photo",
              "Write a personal application message",
              "Be punctual for viewings",
              "Ask questions about the flatshare and flatmates",
              "Bring all required documents",
              "Be honest about your habits",
              "Show interest in the flatshare community",
              "Stay polite even if you get declined",
            ],
          },
          contact: {
            title: "Have more questions?",
            description:
              "Feel free to contact us for more tips and support with your flatshare search.",
            cta: "Contact us",
            ariaLabel: "Contact us via email",
          },
        },
      },
      // 404 Page
      notFound: {
        title: "Page not found",
        description: "The page you are looking for does not exist or has been moved.",
        goHome: "Go to homepage",
        search: "Search WG",
        goBack: "Go back",
        helpfulLinks: "Helpful links",
        searchDescription: "Find your shared apartment room",
        tipsDescription: "Tips for WG search",
        contactInfo: "Problems? Feel free to contact us!",
      },
    },
  },
  fr: {
    translation: {
      // Navigation
      nav: {
        search: "Rechercher",
        offer: "Proposer",
        tips: "Conseils",
        login: "Connexion",
        register: "S'inscrire",
        myAccount: "Mon Compte",
        menu: "Menu",
        darkMode: "Activer le mode sombre",
        lightMode: "Activer le mode clair",
      },
      auth: {
        loginTitle: "Connexion",
        loginDescription: "Connectez-vous pour créer et gérer vos annonces.",
        loginButton: "Se connecter avec votre compte WG-Anzeigen",
        loginHeroEyebrow: "Habiter ensemble, économiser ensemble",
        loginHeroTitle: "Bienvenue sur WG-Anzeigen",
        loginHeroSubtitle: "Gérez vos annonces et trouvez des colocataires depuis un seul espace.",
        loginBenefitListingsTitle: "Toutes vos annonces",
        loginBenefitListingsDescription: "Modifiez vos offres ou créez de nouvelles recherches en quelques clics.",
        loginBenefitSecurityTitle: "Confiance & sécurité",
        loginBenefitSecurityDescription: "Échanges sécurisés avec les membres vérifiés de la communauté WG-Anzeigen.",
        loginBenefitSupportTitle: "Support personnalisé",
        loginBenefitSupportDescription: "Notre équipe vous accompagne pour vos annonces et vos questions.",
        loginHeroFooter: "Votre accès à des offres de colocation fiables partout en Allemagne.",
        forgotPassword: "Mot de passe oublié ?",
      },
      // Header
      header: {
        title: "WG-Anzeigen.de",
        subtitle:
          "Trouvez votre chambre en colocation rapidement et facilement",
        searchPlaceholder: "Entrez ville, code postal ou région",
        roomType: "Chambre / Colocation",
        priceUpTo: "Prix jusqu'à",
        findOffers: "Trouver des offres",
      },
      
      // Listings
      listings: {
        room: "Chambre",
        wg: "Colocation",
        available: "Disponible",
        size: "Taille",
        price: "Prix",
        furnished: "Meublé",
        unfurnished: "Non meublé",
        contact: "Contact",
        verified: "Vérifié",
        online: "En ligne",
        contactNow: "Contacter maintenant",
        contactInfo: "Remplis le formulaire pour contacter le propriétaire.",
        contactForm: {
          title: "Prendre contact",
          subtitle:
            "Présente-toi brièvement et explique ton intérêt pour la chambre.",
          nameLabel: "Ton nom",
          emailLabel: "Ton e-mail",
          messageLabel: "Message",
          messagePlaceholder:
            "Bonjour, je suis intéressé(e) par la chambre...",
          submit: "Envoyer le message",
          submitting: "Envoi...",
          cancel: "Annuler",
          success: "Ton message a été envoyé avec succès.",
          errors: {
            nameRequired: "Merci d'indiquer ton nom.",
            emailRequired: "Merci d'indiquer ton adresse e-mail.",
            emailInvalid: "Merci d'indiquer une adresse e-mail valide.",
            messageRequired: "Merci d'indiquer un message.",
            submitFailed:
              "L'envoi du message a échoué. Réessaie plus tard.",
          },
        },
        minutes: "minutes",
        hours: "heures",
        days: "jours",
      },
      // Search
      search: {
        location: "Lieu",
        roomType: "Type de chambre",
        rentType: "Type de location",
        minSize: "Taille min. m²",
        maxPrice: "Loyer max. €",
        maxPricePlaceholder: "p. ex. 800",
        minSizePlaceholder: "p. ex. 15",
        furnished: "Meublé",
        availableFrom: "Disponible à partir de",
        moreFilters: "Plus de filtres",
        applyFilters: "Appliquer les filtres",
        wgSize: "Taille de la colocation",
        any: "Peu importe",
        popularCitiesLabel: "Villes populaires:",
        clearAllFilters: "Effacer tous les filtres",
      },
      // Footer
      footer: {
        copyright: "© 2024 WG-Anzeigen.de — Équitable, rapide, sans suivi.",
        about: "À propos",
        help: "Aide",
        contact: "Contact",
        privacy: "Confidentialité",
        terms: "CGU",
        imprint: "Mentions légales",
      },
      // Common
      common: {
        loading: "Chargement...",
        error: "Erreur",
        noResults: "Aucun résultat trouvé",
        tryAgain: "Réessayer",
        back: "Retour",
        next: "Suivant",
        previous: "Précédent",
        page: "Page",
        of: "de",
        yes: "Oui",
        no: "Non",
        included: "Inclus",
      },
      tips: {
        heroTitle: "Conseils & guide pour la colocation",
        heroSubtitle: "Tout ce qu'il faut savoir sur la recherche et la vie en colocation",
        alertTitle: "Conseil sécurité",
        alertDescription:
          "Reste prudent face aux annonces de colocation. Visite toujours sur place et ne paie jamais à l'avance sans contrat !",
        sections: {
          search: {
            title: "Conseils pour ta recherche",
            items: [
              {
                title: "Commence ta recherche tôt",
                description:
                  "Lance-toi au moins 2 à 3 mois avant la date d'emménagement souhaitée.",
              },
              {
                title: "Dossier complet",
                description:
                  "Crée un profil convaincant avec photo et une présentation personnelle.",
              },
              {
                title: "Reste flexible",
                description:
                  "Sois ouvert·e à différents quartiers et tailles de colocation.",
              },
              {
                title: "Réagis rapidement",
                description:
                  "Réponds vite aux offres intéressantes – les bonnes chambres partent vite.",
              },
            ],
          },
          security: {
            title: "Sécurité & protection contre la fraude",
            items: [
              {
                title: "Visite avant de signer",
                description:
                  "Visite toujours la chambre en personne avant de signer le contrat.",
              },
              {
                title: "Fais vérifier le contrat",
                description:
                  "Fais relire le bail avant signature (par ex. par une association de locataires).",
              },
              {
                title: "Attention aux avances",
                description:
                  "Ne paie jamais à l'avance sans contrat ou face à des offres douteuses.",
              },
              {
                title: "Rencontre en personne",
                description:
                  "Évite les démarches uniquement par e-mail ou messagerie sans contact direct.",
              },
            ],
          },
          wgLife: {
            title: "Une vie en colocation harmonieuse",
            items: [
              {
                title: "Accords clairs",
                description:
                  "Mettez-vous d'accord dès le début sur le ménage, les achats et les règles communes.",
              },
              {
                title: "Communique",
                description:
                  "Parlez ouvertement des problèmes avant qu'ils ne deviennent des conflits.",
              },
              {
                title: "Activités communes",
                description:
                  "Organise régulièrement des soirées coloc pour mieux vous connaître et renforcer la cohésion.",
              },
              {
                title: "Respect",
                description:
                  "Respecte la vie privée et les habitudes de tes colocataires.",
              },
            ],
          },
          costs: {
            title: "Coûts & finances",
            items: [
              {
                title: "Prévois les charges",
                description:
                  "Intègre l'électricité, Internet, la redevance TV et autres frais dans ton budget.",
              },
              {
                title: "Épargne pour la caution",
                description: "Prévois une caution équivalente à 2 à 3 mois de loyer.",
              },
              {
                title: "Frais d'aménagement",
                description:
                  "Pense aux coûts pour les meubles, l'équipement de cuisine et l'électroménager.",
              },
              {
                title: "Assurances",
                description:
                  "Renseigne-toi sur l'assurance responsabilité civile et habitation.",
              },
            ],
          },
          faq: {
            title: "Questions fréquentes (FAQ)",
            items: [
              {
                question: "Quel budget pour une chambre en colocation ?",
                answer:
                  "En règle générale, ne consacre pas plus de 30 % de ton revenu net au loyer. Dans les grandes villes, 400 à 800 € sont courants.",
              },
              {
                question: "Que mettre dans une bonne candidature ?",
                answer:
                  "Ajoute une photo souriante, une présentation personnelle, tes loisirs, tes études ou ton métier et pourquoi tu corresponds à la coloc.",
              },
              {
                question: "Comment repérer une arnaque ?",
                answer:
                  "Méfie-toi des prix anormalement bas, des paiements anticipés sans contrat, des coordonnées manquantes ou d'une pression à décider vite.",
              },
              {
                question: "Que vérifier lors de la visite ?",
                answer:
                  "Analyse l'emplacement, les transports, l'état de la chambre, les espaces communs, les colocataires et clarifie toutes les questions importantes.",
              },
              {
                question: "Comment résoudre les conflits en coloc ?",
                answer:
                  "Communique ouvertement, écoute, cherche des compromis et planifie des réunions régulières.",
              },
            ],
          },
          quick: {
            title: "Conseils rapides",
            items: [
              "Ajoute une photo de profil attrayante",
              "Rédige un message de candidature personnel",
              "Sois ponctuel·le aux visites",
              "Pose des questions sur la coloc et les colocataires",
              "Apporte tous les documents importants",
              "Sois honnête sur tes habitudes",
              "Montre ton intérêt pour la vie commune",
              "Reste poli·e même en cas de refus",
            ],
          },
          contact: {
            title: "Tu as d'autres questions ?",
            description:
              "Contacte-nous pour davantage de conseils et d'aide dans ta recherche de colocation.",
            cta: "Nous contacter",
            ariaLabel: "Contacter l'équipe par e-mail",
          },
        },
      },
      // 404 Page
      notFound: {
        title: "Page non trouvée",
        description: "La page que vous recherchez n'existe pas ou a été déplacée.",
        goHome: "Aller à l'accueil",
        search: "Rechercher WG",
        goBack: "Retour",
        helpfulLinks: "Liens utiles",
        searchDescription: "Trouvez votre chambre en colocation",
        tipsDescription: "Conseils pour la recherche de WG",
        contactInfo: "Problèmes ? N'hésitez pas à nous contacter !",
      },
    },
  },
  es: {
    translation: {
      // Navigation
      nav: {
        search: "Buscar",
        offer: "Ofrecer",
        tips: "Consejos",
        login: "Iniciar sesión",
        register: "Registrarse",
        myAccount: "Mi Cuenta",
        menu: "Menú",
        darkMode: "Activar modo oscuro",
        lightMode: "Activar modo claro",
      },
      auth: {
        loginTitle: "Iniciar sesión",
        loginDescription: "Accede para crear y gestionar tus anuncios.",
        loginButton: "Inicia sesión con tu cuenta de WG-Anzeigen",
        loginHeroEyebrow: "Vivir juntos, ahorrar juntos",
        loginHeroTitle: "Bienvenido de nuevo a WG-Anzeigen",
        loginHeroSubtitle: "Gestiona tus anuncios y encuentra compañerxs de piso desde un solo panel.",
        loginBenefitListingsTitle: "Todo en un mismo lugar",
        loginBenefitListingsDescription: "Edita anuncios existentes o publica nuevas búsquedas en pocos clics.",
        loginBenefitSecurityTitle: "Confianza y seguridad",
        loginBenefitSecurityDescription: "Mensajería segura con miembros verificados de la comunidad WG-Anzeigen.",
        loginBenefitSupportTitle: "Soporte personal",
        loginBenefitSupportDescription: "Nuestro equipo te ayuda con tus anuncios y dudas sobre la plataforma.",
        loginHeroFooter: "Tu acceso a ofertas de pisos compartidos fiables en toda Alemania.",
        forgotPassword: "¿Has olvidado tu contraseña?",
      },
      // Header
      header: {
        title: "WG-Anzeigen.de",
        subtitle:
          "Encuentra tu habitación en piso compartido rápida y fácilmente",
        searchPlaceholder: "Introduce ciudad, código postal o región",
        roomType: "Habitación / Piso compartido",
        priceUpTo: "Precio hasta",
        findOffers: "Buscar ofertas",
      },
      
      // Listings
      listings: {
        room: "Habitación",
        wg: "Piso compartido",
        available: "Disponible",
        size: "Tamaño",
        price: "Precio",
        furnished: "Amueblado",
        unfurnished: "Sin amueblar",
        contact: "Contacto",
        verified: "Verificado",
        online: "En línea",
        contactNow: "Contactar ahora",
        contactInfo:
          "Rellena el formulario para ponerte en contacto con el propietario.",
        contactForm: {
          title: "Ponerse en contacto",
          subtitle:
            "Preséntate brevemente y explica tu interés por la habitación.",
          nameLabel: "Tu nombre",
          emailLabel: "Tu correo electrónico",
          messageLabel: "Mensaje",
          messagePlaceholder:
            "Hola, estoy interesado/a en la habitación...",
          submit: "Enviar mensaje",
          submitting: "Enviando...",
          cancel: "Cancelar",
          success: "Tu mensaje se ha enviado correctamente.",
          errors: {
            nameRequired: "Introduce tu nombre, por favor.",
            emailRequired: "Introduce tu correo electrónico, por favor.",
            emailInvalid: "Introduce un correo electrónico válido, por favor.",
            messageRequired: "Introduce un mensaje, por favor.",
            submitFailed:
              "No se pudo enviar el mensaje. Inténtalo de nuevo más tarde.",
          },
        },
        minutes: "minutos",
        hours: "horas",
        days: "días",
      },
      // Search
      search: {
        location: "Ubicación",
        roomType: "Tipo de habitación",
        rentType: "Tipo de alquiler",
        minSize: "Tamaño mín. m²",
        maxPrice: "Alquiler máx. €",
        maxPricePlaceholder: "p. ej. 800",
        minSizePlaceholder: "p. ej. 15",
        furnished: "Amueblado",
        availableFrom: "Disponible desde",
        moreFilters: "Más filtros",
        applyFilters: "Aplicar filtros",
        wgSize: "Tamaño de la WG",
        any: "Cualquiera",
        popularCitiesLabel: "Ciudades populares:",
        clearAllFilters: "Borrar todos los filtros",
      },
      // Footer
      footer: {
        copyright: "© 2024 WG-Anzeigen.de — Justo, rápido, sin seguimiento.",
        about: "Acerca de",
        help: "Ayuda",
        contact: "Contacto",
        privacy: "Privacidad",
        terms: "Términos",
        imprint: "Aviso legal",
      },
      // Common
      common: {
        loading: "Cargando...",
        error: "Error",
        noResults: "No se encontraron resultados",
        tryAgain: "Intentar de nuevo",
        back: "Atrás",
        next: "Siguiente",
        previous: "Anterior",
        page: "Página",
        of: "de",
        yes: "Sí",
        no: "No",
        included: "Incluido",
      },
      tips: {
        heroTitle: "Consejos y guía para pisos compartidos",
        heroSubtitle: "Todo lo que necesitas saber sobre la búsqueda y la vida en una WG",
        alertTitle: "Consejo de seguridad",
        alertDescription:
          "Ten siempre cuidado con los anuncios de WG. Visita en persona y nunca pagues por adelantado sin contrato.",
        sections: {
          search: {
            title: "Consejos para tu búsqueda",
            items: [
              {
                title: "Empieza a buscar con tiempo",
                description:
                  "Comienza al menos 2 o 3 meses antes de la fecha en la que quieres mudarte.",
              },
              {
                title: "Solicitud completa",
                description:
                  "Crea un perfil llamativo con foto y una presentación personal.",
              },
              {
                title: "Mantén la flexibilidad",
                description:
                  "Mantén la mente abierta a distintos barrios y tamaños de WG.",
              },
              {
                title: "Responde rápido",
                description:
                  "Contesta enseguida a las ofertas interesantes: las buenas habitaciones vuelan.",
              },
            ],
          },
          security: {
            title: "Seguridad y protección contra fraudes",
            items: [
              {
                title: "Visita antes de firmar",
                description:
                  "Visita siempre la habitación en persona antes de firmar el contrato.",
              },
              {
                title: "Revisa el contrato",
                description:
                  "Haz que revisen el contrato antes de firmarlo (por ejemplo, en una asociación de inquilinos).",
              },
              {
                title: "Cuidado con los pagos por adelantado",
                description:
                  "Nunca pagues por adelantado sin contrato o si la oferta parece sospechosa.",
              },
              {
                title: "Queda en persona",
                description:
                  "Evita acuerdos solo por correo o mensajería sin contacto personal.",
              },
            ],
          },
          wgLife: {
            title: "Convivencia armoniosa",
            items: [
              {
                title: "Acuerdos claros",
                description:
                  "Habla desde el principio sobre limpieza, compras y normas comunes.",
              },
              {
                title: "Comunicación",
                description:
                  "Comenta los problemas abiertamente antes de que se conviertan en conflictos.",
              },
              {
                title: "Actividades conjuntas",
                description:
                  "Organiza noches de WG periódicas para conocerse y crear comunidad.",
              },
              {
                title: "Respeto",
                description:
                  "Respeta la privacidad y los hábitos de tus compañeros de piso.",
              },
            ],
          },
          costs: {
            title: "Costes y finanzas",
            items: [
              {
                title: "Controla los gastos adicionales",
                description:
                  "Incluye electricidad, internet, tasa de televisión y otros gastos en tu presupuesto.",
              },
              {
                title: "Ahorra para la fianza",
                description: "Reserva una fianza de 2 a 3 meses de alquiler.",
              },
              {
                title: "Gastos de mobiliario",
                description:
                  "Ten en cuenta los costes de muebles, equipamiento de cocina y electrodomésticos.",
              },
              {
                title: "Seguros",
                description:
                  "Infórmate sobre seguros de responsabilidad civil y del hogar.",
              },
            ],
          },
          faq: {
            title: "Preguntas frecuentes (FAQ)",
            items: [
              {
                question: "¿Cuánto debería gastar en una habitación de WG?",
                answer:
                  "Como regla general, no destines más del 30 % de tus ingresos netos al alquiler. En grandes ciudades, entre 400 y 800 € es habitual.",
              },
              {
                question: "¿Qué incluye una buena solicitud?",
                answer:
                  "Añade una foto simpática, una presentación personal, tus aficiones, estudios o trabajo y por qué encajas en la WG.",
              },
              {
                question: "¿Cómo detecto ofertas fraudulentas?",
                answer:
                  "Desconfía de precios demasiado bajos, pagos por adelantado sin contrato, datos de contacto incompletos o presión para decidir rápido.",
              },
              {
                question: "¿Qué debo revisar en la visita?",
                answer:
                  "Comprueba la ubicación, el transporte, el estado de la habitación, las zonas comunes, los compañeros y aclara todas las dudas importantes.",
              },
              {
                question: "¿Cómo resuelvo conflictos en la WG?",
                answer:
                  "Habla con sinceridad, escucha, busca compromisos y programa reuniones regulares de WG.",
              },
            ],
          },
          quick: {
            title: "Consejos rápidos",
            items: [
              "Sube una foto de perfil atractiva",
              "Escribe un mensaje de solicitud personal",
              "Llega puntual a las visitas",
              "Haz preguntas sobre la WG y sus integrantes",
              "Lleva toda la documentación necesaria",
              "Sé honesto con tus hábitos",
              "Muestra interés por la comunidad de la WG",
              "Mantén la cortesía incluso si te rechazan",
            ],
          },
          contact: {
            title: "¿Tienes más preguntas?",
            description:
              "Ponte en contacto con nosotros para recibir más consejos y apoyo en tu búsqueda de WG.",
            cta: "Contactar",
            ariaLabel: "Contactar por correo electrónico",
          },
        },
      },
      // 404 Page
      notFound: {
        title: "Página no encontrada",
        description: "La página que buscas no existe o ha sido movida.",
        goHome: "Ir al inicio",
        search: "Buscar WG",
        goBack: "Volver",
        helpfulLinks: "Enlaces útiles",
        searchDescription: "Encuentra tu habitación en piso compartido",
        tipsDescription: "Consejos para buscar WG",
        contactInfo: "¿Problemas? ¡Contáctanos!",
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: localStorage.getItem("wg_lang") || "de",
  fallbackLng: "de",
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
